<script context="module">
    import { get } from "svelte/store";
    import {
        user,
        createUser,
        showApiErrorModal,
        isRetriedFetch,
        firstFetchErrorMsg,
        loggedInDoctorDetail,
    } from "../stores";
    import { HTTP_STATUS, X_Device_ID } from "../Utils/utilConstants.svelte";
    import {
        globalAbortController,
        refreshToken,
        showError,
    } from "../Utils/utilFunctions.svelte";
    import {
        serverUrl,
        checkTokenExpiration,
        signOut,
    } from "../Utils/utilFunctions.svelte";

    const noAuthUrlArr = ["FORGOTPASSWORD", "SSO"];

    // UPPER CASE BEING COMPARED
    const stringResponseApi = [
        "REPORTS/ENCOUNTERSSUMMARY",
        "REPORTS/PAYMENTSSUMMARY",
    ];

    const doFetch = async (url, isManuallyHandleError = false) => {
        const userData = get(user);
        const userToken = userData == null ? "" : userData.token;

        const createUserData = get(createUser);
        const createUserToken =
            createUserData == null ? "" : createUserData.token;

        if (
            userToken ||
            createUserToken ||
            noAuthUrlArr.includes(url.split("?")[0].toUpperCase())
        ) {
            const tokenRefreshResult = noAuthUrlArr.includes(
                url.split("?")[0].toUpperCase(),
            )
                ? "valid"
                : await checkTokenExpiration();

            if (
                tokenRefreshResult != "valid" &&
                tokenRefreshResult != "refreshed"
            ) {
                signOut({ reason: "Invalid token" }, true);
                return;
            }

            try {
                var requestObject = {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Access-Control-Allow-Credentials": "true",
                        "Access-Control-Allow-Origin": "*",
                        "Access-Control-Allow-Methods": "GET",
                        Authorization: `Bearer ${
                            userToken ? userToken : createUserToken
                        }`,
                    },
                    mode: "cors",
                    cache: "no-cache",
                    signal: globalAbortController.signal,
                };

                requestObject.headers["X-Device-Id"] = X_Device_ID;

                const response = await fetch(
                    `${serverUrl}/${url}`,
                    requestObject,
                );

                if (response.ok) {
                    if (response.status === HTTP_STATUS.NO_CONTENT) return null;

                    const isUrlResponseInString = stringResponseApi.some(
                        (api) => url.toUpperCase().includes(api),
                    );

                    let res = isUrlResponseInString
                        ? response
                        : await response.json();

                    if (res.error && !isManuallyHandleError) {
                        console.log(res.error);
                        showError(
                            "Please refresh the page and try again and contact support if the issue persists.",
                        );
                    }

                    return res;
                } else if (
                    response.status == HTTP_STATUS.SERVER_TIMEOUT ||
                    response.status == HTTP_STATUS.BAD_GATE_WAY ||
                    response.status == HTTP_STATUS.SERVICE_UNAVAILABLE ||
                    response.status == HTTP_STATUS.GATEWAY_TIMEOUT
                ) {
                    // wait 1s and retry
                    // if fails with same error show alert instead of log out
                    if (get(isRetriedFetch)) {
                        isRetriedFetch.set(false);
                        firstFetchErrorMsg.set("");
                        showApiErrorModal.set(true);
                        return;
                    }

                    isRetriedFetch.set(true);
                    const resText = await response.text();
                    firstFetchErrorMsg.set(resText);

                    setTimeout(async () => {
                        await refreshToken();
                        return await doFetch(url);
                    }, 1000);
                } else if (
                    response.status == HTTP_STATUS.UNAUTHORIZED ||
                    response.status == HTTP_STATUS.FORBIDDEN
                ) {
                    const tokenStatus = await checkTokenExpiration();

                    if (tokenStatus === "valid") {
                        const currentUserData = get(user);
                        if (
                            currentUserData &&
                            currentUserData.token &&
                            currentUserData.token !== userToken
                        ) {
                            return await doFetch(url);
                        }

                        if (get(isRetriedFetch)) {
                            signOut(
                                { url, errorStatus: response.status },
                                true,
                            );
                            isRetriedFetch.set(false);
                            return;
                        }

                        const localSavedUserData = JSON.parse(
                            localStorage.getItem("TciSavedUser"),
                        );
                        if (localSavedUserData) {
                            user.set({
                                ...localSavedUserData,
                            });
                        }

                        await new Promise((resolve) =>
                            setTimeout(resolve, 1000),
                        );

                        isRetriedFetch.set(true);

                        const refreshStatus = await refreshToken();

                        if (refreshStatus) {
                            return await doFetch(url);
                        } else {
                            signOut(
                                { url, errorStatus: response.status },
                                true,
                            );
                            return;
                        }
                    } else if (tokenStatus === "refreshed") {
                        return await doFetch(url);
                    } else {
                        return;
                    }
                } else {
                    const errText = await response.text();
                    throw new Error(errText);
                }
            } catch (error) {
                throw error;
            }
        } else signOut({ reason: "Invalid token" }, true);
    };

    const getAddressFromGoogle = async (query = "") => {
        if (!query) return [];

        const displayAddress = (predictions, status) => {
            // callback function if needed in the future
        };

        const service = new google.maps.places.AutocompleteService();

        const addressParams = {
            input: query,
            componentRestrictions: {
                country: "au",
            },
        };

        const response = await service.getPlacePredictions(
            {
                ...addressParams,
            },
            displayAddress,
        );

        return await response;
    };

    const getPlaceDetailsFromGoogle = async (placeId = "") => {
        if (!placeId) return;

        return new Promise((resolve, reject) => {
            const getPlaceDetails = (place, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK)
                    resolve(place);
                else reject(status);
            };

            // element to avoid console error trying to read innerHTML
            const service = new google.maps.places.PlacesService(
                document.createElement("div"),
            );

            const addressParams = {
                placeId,
            };

            service.getDetails(addressParams, getPlaceDetails);
        });
    };

    const getResetPwd = async (params) => {
        let url = "ForgotPassword";

        if (params && Object.keys(params).length) {
            url += "?";
            for (const param in params) url += `&${param}=${params[param]}`;
        }

        return await doFetch(url);
    };

    const getAdmin = async (start = Number(0), limit = Number(25), params) => {
        let url = `Admin?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getAdminById = async (id) => {
        let url = `Admin/${id}`;

        return await doFetch(url);
    };

    const getAccount = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `Account?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getAccountById = async (id) => {
        let url = `Account/${id}`;

        return await doFetch(url);
    };

    const getAccountPrePopulateSearchOptions = async () => {
        let url = `Account/GetForFilter?limit=999&filter[status]=0,1`;

        return await doFetch(url);
    };

    const getAccountByUserId = async (id) => {
        let url = `User/${id}`;

        const res = await doFetch(url);

        if (res && res.userAccounts && res.userAccounts.length) {
            const accountId = res.userAccounts.find((acc) => acc.id).id;
            return getAccountById(accountId);
        }
    };

    const getAccountProvider = async (id) => {
        let url = `AccountProvider/?filter[doctorid]=${id}&limit=99`;

        return await doFetch(url);
    };

    const getAccountAvatar = async (id) => {
        let url = `image?filter[doctorId]=${id}&filter[type]=2`;

        return await doFetch(url);
    };

    const getAccountAvatarForPatient = async (id) => {
        let url = `image?filter[doctorId]=${id}&filter[type]=3`;

        return await doFetch(url);
    };

    const getAccountAvatarWithType = async (id, type) => {
        let url = `image?filter[doctorId]=${id}&filter[type]=${type}`;
        return await doFetch(url);
    };

    const getGroups = async (start = Number(0), limit = Number(25), params) => {
        let url = `Group?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getGroupById = async (id) => {
        let url = `Group/${id}`;

        return await doFetch(url);
    };

    const getExternalGroups = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `ExternalGroup?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getExternalGroupById = async (id) => {
        let url = `ExternalGroup/${id}`;

        return await doFetch(url);
    };

    const getExternalGroupInvoices = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `ExternalGroupInvoice?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getExternalGroupInvoiceById = async (id) => {
        let url = `ExternalGroupInvoice/${id}`;

        return await doFetch(url);
    };

    const getAccountActiveInsuranceFund = async (accountId) => {
        let url = `AccountInsuranceFundActive/${accountId}`;

        return await doFetch(url);
    };

    const getAccountUsage = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `AccountUsage?start=${start}&limit=${limit}`;

        if (params.search) url += `&search=${params.search}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) {
                if (filter !== "search") url += `&${filter}=${params[filter]}`;
            }
        }

        return await doFetch(url);
    };

    const getAccountInvoice = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `AccountInvoice?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getDeletedProviders = async (id) => {
        let url = `AccountProvider?filter[doctorid]=${id}&filter[status]=9&limit=999`;

        return await doFetch(url);
    };

    const getAccountInvoiceById = async (id) => {
        let url = `AccountInvoice/${id}`;

        return await doFetch(url);
    };

    const getAccountReportsById = async (id) => {
        let url = `AccountInvoice/Reports/${id}`;

        return await doFetch(url);
    };

    const getMBSItems = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `Mbs?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getMBSItemsWithoutRebate = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `Mbs/noRebates?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getNoGapProgramRebates = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `Mbs/getnogaprebates?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getNoGapProgramRebatesTablesName = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `Mbs/getnogaprebatestablenames?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getMBSItemsLogic = async () => {
        let url = `Mbs/getLogic`;

        return await doFetch(url);
    };

    const getMBSItemByItemId = async (id) => {
        let url = `Mbs/${id}`;

        return await doFetch(url);
    };

    const getMbsStricturedTypes = async () => {
        let url = `mbs/getStructuredTypes`;

        return await doFetch(url);
    };

    const getMbsTypes = async () => {
        let url = `mbs/getTypes`;

        return await doFetch(url);
    };

    const getMbsGroups = async () => {
        let url = `mbs/getGroups`;

        return await doFetch(url);
    };

    const getMbsRegions = async () => {
        let url = `mbs/getRegions`;

        return await doFetch(url);
    };

    const getMbsSubRegions = async () => {
        let url = `mbs/getSubregions`;

        return await doFetch(url);
    };

    const getASAItems = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `Asa?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getASAItemsLogic = async () => {
        let url = `Asa/getLogic`;

        return await doFetch(url);
    };

    const getASAItemByItemId = async (id) => {
        let url = `Asa/${id}`;

        return await doFetch(url);
    };

    const getAsaStricturedTypes = async () => {
        let url = `asa/getStructuredTypes`;

        return await doFetch(url);
    };

    const getAsaTypes = async () => {
        let url = `asa/getTypes`;

        return await doFetch(url);
    };

    const getAsaGroups = async () => {
        let url = `asa/getGroups`;

        return await doFetch(url);
    };

    const getAsaRegions = async () => {
        let url = `asa/getRegions`;

        return await doFetch(url);
    };

    const getAsaSubRegions = async () => {
        let url = `asa/getSubregions`;

        return await doFetch(url);
    };

    const getItemsLogic = async () => {
        let url = `Logic`;

        return await doFetch(url);
    };

    const getEmailTemplates = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `EmailTemplate?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getEmailTemplateById = async (id) => {
        let url = `EmailTemplate/${id}`;
        return await doFetch(url);
    };

    const getSmsTemplates = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `SmsTemplate?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getSmsTemplateById = async (id) => {
        let url = `SmsTemplate/${id}`;
        return await doFetch(url);
    };

    const getPdfTemplates = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `PdfTemplate?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getPdfTemplateById = async (id) => {
        let url = `PdfTemplate/${id}`;
        return await doFetch(url);
    };

    const getPatient = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `Patient?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getPatientById = async (id) => {
        let url = `Patient/${id}`;

        return await doFetch(url);
    };

    const getPatientContact = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `PatientContact?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getPatientContactById = async (id) => {
        return await getPatientContact(0, 999999, { "filter[patientId]": id });
    };

    const getPatientEncounter = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `PatientEncounter?start=${start}&limit=${limit}`;

        // filter[simplified]=1 will remove invoices, estimates and billingData

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getPatientEncounterById = async (id, includeCancelled) => {
        let url = `PatientEncounter/${id}`;

        if (includeCancelled) url += "?filter[includecancelledbillingitems]=1";

        return await doFetch(url);
    };

    const getPatientEncounterFundData = async (billingDataId) => {
        if (!billingDataId) return;

        let url = `PatientEncounterSummary/FundData/${billingDataId}`;

        return await doFetch(url);
    };

    const getPatientQuote = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `PatientEncounter?start=${start}&limit=${limit}&filter[quoteOnly]=1`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getUserById = async (id) => {
        let url = `User/${id}`;

        return await doFetch(url);
    };

    const getInsuranceFund = async (
        start = Number(0),
        limit = Number(25),
        params = {},
    ) => {
        let url = `InsuranceFund?start=${start}&limit=${limit}`;

        if (params.search) url += `&search=${params.search}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) {
                if (filter !== "search") url += `&${filter}=${params[filter]}`;
            }
        }

        return await doFetch(url);
    };

    const getInsuranceFundById = async (id) => {
        let url = `InsuranceFund/${id}`;

        return await doFetch(url, true);
    };

    const getInsuranceFundUnitValue = async (id) => {
        let url = `InsuranceFund/FundUnitValue/${id}`;

        return await doFetch(url, true);
    };

    const getWorkCoverAuthorities = async (
        start = Number(0),
        limit = Number(25),
        params = {},
    ) => {
        let url = `WorkCoverAuthorities?start=${start}&limit=${limit}`;

        if (params.search) url += `&search=${params.search}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) {
                if (filter !== "search") url += `&${filter}=${params[filter]}`;
            }
        }

        return await doFetch(url);
    };

    const getWorkCoverAuthorityById = async (id) => {
        let url = `WorkCoverAuthorities/${id}`;

        return await doFetch(url, true);
    };

    const getTrafficAccidentAuthorities = async (
        start = Number(0),
        limit = Number(25),
        params = {},
    ) => {
        let url = `TrafficAccidentAuthorities?start=${start}&limit=${limit}`;

        if (params.search) url += `&search=${params.search}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) {
                if (filter !== "search") url += `&${filter}=${params[filter]}`;
            }
        }

        return await doFetch(url);
    };

    const getHospitals = async (
        start = Number(0),
        limit = Number(25),
        params = {},
    ) => {
        let url = `Hospital?start=${start}&limit=${limit}`;

        if (params.search) url += `&search=${params.search}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) {
                if (filter !== "search") url += `&${filter}=${params[filter]}`;
            }
        }

        return await doFetch(url);
    };

    const getHospitalById = async (id) => {
        let url = `Hospital/${id}`;

        return await doFetch(url);
    };

    const getHospitalsNoGapProgram = async (
        start = Number(0),
        limit = Number(25),
        params = {},
    ) => {
        let url = `HospitalsNoGapProgram?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) {
                if (filter !== "search") url += `&${filter}=${params[filter]}`;
            }
        }

        return await doFetch(url);
    };

    const getHospitalsNoGapProgramById = async (id) => {
        let url = `HospitalsNoGapProgram/${id}`;

        return await doFetch(url);
    };

    const getAccountDoctors = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `AccountDoctor?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) {
                url += `&${filter}=${params[filter]}`;
            }
        }

        return await doFetch(url);
    };

    const getAccountDoctorById = async (id) => {
        let url = `AccountDoctor/${id}`;

        return await doFetch(url);
    };

    const getEstimates = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `Estimate?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) {
                url += `&${filter}=${params[filter]}`;
            }
        }

        return await doFetch(url);
    };

    const getEstimateById = async (id) => {
        let url = `Estimate/${id}`;

        return await doFetch(url);
    };

    const getInvoices = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `Invoice?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) {
                url += `&${filter}=${params[filter]}`;
            }
        }

        return await doFetch(url);
    };

    const getInvoiceById = async (id) => {
        let url = `Invoice/${id}`;

        return await doFetch(url);
    };

    const getFile = async (type, id) => {
        let url = `File/${type}/${id}`;

        return await doFetch(url);
    };

    const getFileById = async (id) => {
        let url = `File/${id}`;

        return await doFetch(url);
    };

    const getAllProviders = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `AllProvider?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) {
                url += `&filter[${filter}]=${params[filter]}`;
            }
        }

        return await doFetch(url);
    };

    const getEclipseLogs = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `EclipseLog?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getEclipseLogById = async (id) => {
        let url = `EclipseLog/${id}`;

        return await doFetch(url);
    };

    const getActivityLogs = async (
        start = Number(0),
        limit = Number(25),
        params = {},
    ) => {
        let url = `ActivityLog?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getActivityLogById = async (id) => {
        let url = `ActivityLog/${id}`;

        return await doFetch(url);
    };

    const getExceptionLogs = async (
        start = Number(0),
        limit = Number(25),
        params = {},
    ) => {
        let url = `ExceptionLog?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getExceptionLogById = async (id) => {
        let url = `ExceptionLog/${id}`;

        return await doFetch(url);
    };

    const getLoginLogs = async (
        start = Number(0),
        limit = Number(25),
        params = {},
    ) => {
        let url = `LoginLog?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getUnauthorisedLogs = async (
        start = Number(0),
        limit = Number(25),
        params = {},
    ) => {
        let url = `UnauthorisedLog?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getUnauthorisedLogById = async (id) => {
        let url = `UnauthorisedLog/${id}`;

        return await doFetch(url);
    };

    const getActivityAndEncounterLogsById = async (id, extended = false) => {
        let url = `ActivityLog/encounter${extended ? "extended" : ""}/${id}`;

        return await doFetch(url);
    };

    const getReceipts = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `Receipt?start=${start}&limit=${limit}`;

        if (params.search) url += `&search=${params.search}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) {
                if (filter !== "search") url += `&${filter}=${params[filter]}`;
            }
        }

        return await doFetch(url);
    };

    const getReceiptById = async (id) => {
        const url = `Receipt/${id}`;

        return await doFetch(url);
    };

    const getReconciliation = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `Reconciliation?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getReconciliationERA = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `ReconciliationERA?start=${start}&limit=${limit}`;

        if (params.search) url += `&search=${params.search}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) {
                if (filter !== "search") url += `&${filter}=${params[filter]}`;
            }
        }

        return await doFetch(url);
    };

    const getReconciliationERAbyId = async (id) => {
        let url = `ReconciliationERA/${id}`;

        return await doFetch(url);
    };

    const getReconciliationEFT = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `ReconciliationEFT?start=${start}&limit=${limit}`;

        if (params.search) url += `&search=${params.search}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) {
                if (filter !== "search") url += `&${filter}=${params[filter]}`;
            }
        }

        return await doFetch(url);
    };

    const getReconciliationEFTbyId = async (id) => {
        let url = `ReconciliationEFT/${id}`;

        return await doFetch(url);
    };

    const getDashboardDataFromDoctor = async (params) => {
        let url = `Insights`;

        let query = "";
        if (params && Object.keys(params).length) {
            for (const filter in params) {
                if (query == "") query += "?";
                else query += "&";
                query += `${filter}=${params[filter]}`;
            }
        }
        return await doFetch(`${url}${query}`);
    };

    const getDashboardDataForAdminAndManager = async (
        params,
        doctorId = Number(0),
    ) => {
        let url = `Insights/admin/${doctorId}`;
        let query = "";
        if (params && Object.keys(params).length) {
            for (const filter in params) {
                if (query == "") query += "?";
                else query += "&";
                query += `${filter}=${params[filter]}`;
            }
        }
        return await doFetch(`${url}${query}`);
    };

    const getDashboardDataForDoctor = async (params) => {
        if (!get(loggedInDoctorDetail)?.id) return;

        const doctorId = get(loggedInDoctorDetail).id;
        let url = `Insights/doctor/${doctorId}`;
        let query = `?filter[doctorId]=${doctorId}`;
        if (params && Object.keys(params).length) {
            for (const filter in params)
                query += `&${filter}=${params[filter]}`;
        }

        return await doFetch(`${url}${query}`);
    };

    const getProviders = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `AllProvider?start=${start}&limit=${limit}`;

        if (params.search) url += `&search=${params.search}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) {
                if (filter !== "search") url += `&${filter}=${params[filter]}`;
            }
        }

        return await doFetch(url);
    };

    const getAllProviderByUniqueId = async (uniqueId) => {
        let url = `AllProvider/${uniqueId}`;
        return await doFetch(url);
    };

    const getProvidersSpecialityCodes = async () => {
        let url = `AllProvider/GetSpecialityCodes`;

        return doFetch(url);
    };

    const getOcrList = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `OcrSchedule?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) {
                url += `&${filter}=${params[filter]}`;
            }
        }

        return await doFetch(url);
    };

    const getOcrListById = async (id) => {
        let url = `OcrSchedule/${id}`;

        return doFetch(url);
    };

    const getAnnouncements = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `Announcement?start=${start}&limit=${limit}`;

        if (params.search) url += `&search=${params.search}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) {
                if (filter !== "search") url += `&${filter}=${params[filter]}`;
            }
        }

        return await doFetch(url);
    };

    const getOrderProducts = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `OrderProducts?start=${start}&limit=${limit}`;

        if (params.search) url += `&search=${params.search}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) {
                if (filter !== "search") url += `&${filter}=${params[filter]}`;
            }
        }

        return await doFetch(url);
    };

    const getAgreementDocuments = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `AgreementDocuments?start=${start}&limit=${limit}`;

        if (params?.search) url += `&search=${params.search}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) {
                if (filter !== "search") url += `&${filter}=${params[filter]}`;
            }
        }

        return await doFetch(url);
    };

    const getAnnouncementById = async (id) => {
        let url = `Announcement/${id}`;

        return await doFetch(url);
    };

    const getManager = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `Manager?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getManagerById = async (id) => {
        let url = `Manager/${id}`;

        return await doFetch(url);
    };

    const getGlobalEvents = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `GlobalCalendarEvent?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getGlobalEventById = async (id) => {
        let url = `GlobalCalendarEvent/${id}`;

        return await doFetch(url);
    };

    const getPublicHolidays = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `PublicHoliday?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getSurgerySuggestedItems = async (procedureName = "", doctorId = 0) => {
        var procedureNameEncoded = procedureName.replaceAll("/", " "); //replace / with ' '
        procedureNameEncoded = encodeURIComponent(
            procedureNameEncoded,
        ).replaceAll("%20", " "); //do not encode spaces

        let url = `suggestions/mbsAsaSuggestionsByDoctorAndName?name=${procedureNameEncoded}${doctorId != 0 ? `&doctorId=${doctorId}`: ''}`;

        return await doFetch(url);
    };

    const getAccountSchedules = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `AccountSchedule?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getAccountSchedulePDF = async (pdfID) => {
        let url = `AccountSchedule/${pdfID}`;

        return await doFetch(url);
    };

    const getAccountLeaves = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `AccountLeave?start=${start}&limit=${limit}`;

        if (params.search) url += `&search=${params.search}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) {
                if (filter !== "search") url += `&${filter}=${params[filter]}`;
            }
        }

        return await doFetch(url);
    };

    // get account favorite surgeon/referral
    const getSuggestedSurgeon = async (accountId) => {
        let url = `Suggestions/doctorsAccountDoctors/${accountId}`;

        return await doFetch(url);
    };

    const getSuggestedHospital = async (accountId) => {
        let url = `Suggestions/doctorsHospitals/${accountId}`;

        return await doFetch(url);
    };

    const getSupportStaff = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `SupportStaff?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getSupportStaffById = async (id) => {
        let url = `SupportStaff/${id}`;

        return await doFetch(url);
    };

    const getBupaPractices = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `BupaPracticeId?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getBupaPracticeById = async (id) => {
        let url = `BupaPracticeId/${id}`;

        return await doFetch(url);
    };

    const getAccountCalendarEvents = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `AccountCalendarEvent?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getEventById = async (id) => {
        let url = `AccountCalendarEvent/${id}`;

        return await doFetch(url);
    };

    const getEncounterCommunications = async (id) => {
        let url = `PatientEncounter/EncounterNotifications/${id}`;
        return await doFetch(url);
    };

    const getEncounterCommunication = async (encounterId, notificationId) => {
        let url = `PatientEncounter/EncounterNotification/${encounterId}/${notificationId}`;
        return await doFetch(url);
    };

    const getEncounterPreOpQuestionnaireAnswer = async (encounterId) => {
        let url = `PreOpQuestionnaire/GetAnswers/${encounterId}`;

        return await doFetch(url);
    };

    const getEncounterPostOpSurveyAnswer = async (encounterId) => {
        let url = `PostOpSurvey/GetAnswers/${encounterId}`;

        return await doFetch(url);
    };

    const getNumberOfFollowUpItems = async () => {
        let url = `PatientEncounterSummary/FollowUpTotals`;

        return await doFetch(url);
    };

    const getNotes = async (relType, relID) => {
        let url = `notes/${relType}/${relID}?sort=createddatedesc`;
        return await doFetch(url);
    };

    const getMultipleEncounterNotes = async (encounterIds = []) => {
        if (!encounterIds.length) return;

        let url = `notes/Encounters?limit=999999&filter[relId]=${encounterIds.join().trim()}`;

        return await doFetch(url);
    };

    const getSSOPermission = async (sso = "") => {
        if (!sso) return;

        let url = `SSO?sso=${sso}`;

        return await doFetch(url);
    };

    const getPreOpInstructions = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `PreOpInstructions?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getPreOpInstructionById = async (id) => {
        let url = `PreOpInstructions/${id}`;

        return await doFetch(url);
    };

    const getPreOpQuestionnaires = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `PreOpQuestionnaire?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getPreOpQuestionnaireById = async (id) => {
        let url = `PreOpQuestionnaire/${id}`;

        return await doFetch(url);
    };

    const getPostOpSurvey = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `PostOpSurvey?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getAccountPresets = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `AccountPresets?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getAccountPresetById = async (id) => {
        let url = `AccountPresets/${id}`;

        return await doFetch(url);
    };

    const getBillingRules = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `BillingRule?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getBillingRuleById = async (id) => {
        let url = `BillingRule/${id}`;

        return await doFetch(url);
    };

    const getOcrStickers = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `OcrSticker?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getOcrStickerById = async (id) => {
        let url = `OcrSticker/${id}`;

        return await doFetch(url);
    };

    const getThirdPartyInsurers = async (
        start = Number(0),
        limit = Number(99),
        params,
    ) => {
        let url = `AccountThirdPartyInsurers?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getThirdPartyInsurerSuggestions = async (doctorId) => {

        let url = `Suggestions/DoctorsThirdPartyInsurers/${doctorId}`;

        return await doFetch(url);
    };

    const getCalendarEventsCategories = async (
        start = Number(0),
        limit = Number(99),
        params,
    ) => {
        let url = `CalendarEventsCategories?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getAccountAvailability = async (params) => {
        let url = `AccountAvailability?start=${0}&limit=${99999}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getDefaultFilters = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `UsersFilters?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    // types:
    // EmailTemplate
    // SmsTemplate
    // PdfTemplate
    const getTemplateTypes = async (controllerType) => {
        if (!controllerType) return [];
        let url = `${controllerType}/GetTypes`;
        return await doFetch(url);
    };

    const getDefaultFilterById = async (id) => {
        if (!id) return;

        return await doFetch(`UsersFilters/${id}`);
    };

    const getSurgeryRooms = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `SurgeryRooms?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getSurgeryRoomById = async (id) => {
        let url = `SurgeryRooms/${id}`;

        return await doFetch(url);
    };

    const getEncounterFreeTextItems = async (encounterId, billingId) => {
        if (!encounterId || !billingId) return;

        let url = `PatientEncounterCode/${encounterId}/${billingId}`;

        return await doFetch(url);
    };

    const getLogWithTypeByID = async (type, id) => {
        let url = `${type}Log/${id}`;

        return await doFetch(url);
    };

    const getAccountInsuranceDefaultCover = async (accountId) => {
        let url = `AccountInsuranceDefaultCover/${accountId}`;

        return await doFetch(url);
    };

    const getAccountNotifications = async (id, params) => {
        let url = `Account/AccountNotifications/${id}`;

        if (params && Object.keys(params).length) {
            url += "?";
            for (const param in params) url += `&${param}=${params[param]}`;
        }

        return await doFetch(url);
    };

    const getPaymentReports = async (params) => {
        let url = "Reports/PaymentsSummary";

        if (params && Object.keys(params).length) {
            url += "?";
            for (const param in params) url += `&${param}=${params[param]}`;
        }

        return await doFetch(url);
    };

    const getEncounterReports = async (params) => {
        let url = "Reports/EncountersSummary";

        if (params && Object.keys(params).length) {
            url += "?";
            for (const param in params) url += `&${param}=${params[param]}`;
        }

        return await doFetch(url);
    };

    const getPortalCustomisationConfigByGroupId = async (groupId) => {
        let url = `portal/PortalCustomisation/${groupId}`;

        return await doFetch(url);
    };

    const getAccountThirdPartyInsurers = async (
        start = Number(0),
        limit = Number(25),
        params,
    ) => {
        let url = `AccountThirdPartyInsurers?start=${start}&limit=${limit}`;

        if (params && Object.keys(params).length) {
            for (const filter in params) url += `&${filter}=${params[filter]}`;
        }

        return await doFetch(url);
    };

    const getAccountThirdPartyInsurerById = async (id) => {
        if (!id) return;

        let url = `AccountThirdPartyInsurers/${id}`;

        return await doFetch(url);
    };

    const getAccountProviderNoGapPrograms = async (doctorId, doctorProviderId) => {
        if (!doctorId || !doctorProviderId) return;

        let url = `AccountProviderNoGapProgram/${doctorId}/${doctorProviderId}`;

        return await doFetch(url);
    };

    const getNoGapProgramNames = async (hospitalId, fundId) => {
        if (!hospitalId || !fundId) return;

        let url = `HospitalsNoGapProgram/getNoGapProgramNames/${hospitalId}/${fundId}`;

        return await doFetch(url);
    };

    const getEstimateDueDateByBillingItem = async (billingDataId) => {
        if(!billingDataId) return;

        let url = `Estimate/GetEstimateDueDateByBillingItem/${billingDataId}`;

        return await doFetch(url);
    }

    const getInvoiceDueDateByBillingItem = async (billingDataId) => {
        if(!billingDataId) return;
        
        let url = `Invoice/GetInvoiceDueDateByBillingItem/${billingDataId}`;

        return await doFetch(url);
    }

    export const getMethod = {
        getEstimateDueDateByBillingItem,
        getInvoiceDueDateByBillingItem,
        getAddressFromGoogle,
        getPlaceDetailsFromGoogle,
        getResetPwd,
        getAdmin,
        getAdminById,
        getAccount,
        getAccountById,
        getAccountPrePopulateSearchOptions,
        getAccountByUserId,
        getAccountAvatar,
        getAccountAvatarForPatient,
        getGroups,
        getGroupById,
        getExternalGroups,
        getExternalGroupById,
        getExternalGroupInvoices,
        getExternalGroupInvoiceById,
        getAccountActiveInsuranceFund,
        getAccountUsage,
        getAccountInvoice,
        getAccountInvoiceById,
        getPatient,
        getPatientById,
        getPatientContact,
        getPatientContactById,
        getPatientEncounter,
        getPatientEncounterById,
        getPatientEncounterFundData,
        getPatientQuote,
        getUserById,
        getInsuranceFund,
        getInsuranceFundById,
        getInsuranceFundUnitValue,
        getWorkCoverAuthorities,
        getWorkCoverAuthorityById,
        getTrafficAccidentAuthorities,
        getHospitals,
        getHospitalById,
        getHospitalsNoGapProgram,
        getHospitalsNoGapProgramById,
        getAccountDoctors,
        getAccountDoctorById,
        getEstimates,
        getEstimateById,
        getInvoices,
        getInvoiceById,
        getFile,
        getFileById,
        getAllProviders,
        getEclipseLogs,
        getEclipseLogById,
        getActivityLogs,
        getActivityLogById,
        getExceptionLogs,
        getExceptionLogById,
        getLoginLogs,
        getUnauthorisedLogs,
        getUnauthorisedLogById,
        getActivityAndEncounterLogsById,
        getReceipts,
        getReceiptById,
        getReconciliation,
        getReconciliationERA,
        getReconciliationERAbyId,
        getReconciliationEFT,
        getReconciliationEFTbyId,
        getDashboardDataFromDoctor,
        getDashboardDataForAdminAndManager,
        getDashboardDataForDoctor,
        getProviders,
        getAllProviderByUniqueId,
        getProvidersSpecialityCodes,
        getOcrList,
        getOcrListById,
        getMBSItems,
        getMBSItemsWithoutRebate,
        getMBSItemsLogic,
        getMbsStricturedTypes,
        getMbsTypes,
        getMbsGroups,
        getMbsRegions,
        getMbsSubRegions,
        getMBSItemByItemId,
        getASAItems,
        getASAItemsLogic,
        getASAItemByItemId,
        getAsaStricturedTypes,
        getAsaTypes,
        getAsaGroups,
        getAsaRegions,
        getAsaSubRegions,
        getItemsLogic,
        getEmailTemplates,
        getEmailTemplateById,
        getSmsTemplates,
        getSmsTemplateById,
        getPdfTemplates,
        getPdfTemplateById,
        getAnnouncements,
        getAnnouncementById,
        getManager,
        getManagerById,
        getGlobalEvents,
        getGlobalEventById,
        getPublicHolidays,
        getAccountSchedules,
        getAccountSchedulePDF,
        getAccountLeaves,
        getSuggestedSurgeon,
        getSuggestedHospital,
        getSurgerySuggestedItems,
        getDeletedProviders,
        getSupportStaff,
        getSupportStaffById,
        getBupaPractices,
        getBupaPracticeById,
        getAccountCalendarEvents,
        getEncounterCommunications,
        getEncounterCommunication,
        getAccountProvider,
        getEncounterPreOpQuestionnaireAnswer,
        getEncounterPostOpSurveyAnswer,
        getNumberOfFollowUpItems,
        getAccountAvatarWithType,
        getNotes,
        getMultipleEncounterNotes,
        getSSOPermission,
        getPreOpInstructions,
        getPreOpInstructionById,
        getPreOpQuestionnaires,
        getPreOpQuestionnaireById,
        getPostOpSurvey,
        getAccountPresets,
        getAccountPresetById,
        getBillingRules,
        getBillingRuleById,
        getOcrStickers,
        getOcrStickerById,
        getThirdPartyInsurers,
        getThirdPartyInsurerSuggestions,
        getCalendarEventsCategories,
        getAccountAvailability,
        getTemplateTypes,
        getDefaultFilters,
        getDefaultFilterById,
        getEventById,
        getSurgeryRooms,
        getSurgeryRoomById,
        getEncounterFreeTextItems,
        getLogWithTypeByID,
        getAccountReportsById,
        getAccountInsuranceDefaultCover,
        getAccountNotifications,
        getPaymentReports,
        getEncounterReports,
        getPortalCustomisationConfigByGroupId,
        getAccountThirdPartyInsurers,
        getAccountThirdPartyInsurerById,
        getAccountProviderNoGapPrograms,
        getNoGapProgramNames,
        getAgreementDocuments,
        getOrderProducts,
        getNoGapProgramRebates,
        getNoGapProgramRebatesTablesName
    };
</script>

<script src="https://polyfill.io/v3/polyfill.min.js?features=default">
</script>
